import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, FolderKanban, Pencil, Plus, Search, User, Users, X } from 'lucide-react';
import { toast } from 'react-toastify';
import AdminLayout from '../components/admin/AdminLayout';
import AddEmployeeToProjectModal from '../components/admin/AddEmployeeToProjectModal';
import EditProjectUserRoleModal from '../components/admin/EditProjectUserRoleModal';
import RemoveProjectUserConfirmModal from '../components/admin/RemoveProjectUserConfirmModal';
import workspaceService from '../services/workspaceService';
import {
  getAdminCompanies,
  getAdminProjectUsers,
  getAdminProjectUserCounts,
  getAdminProjects,
  inviteAdminEmployee,
  applyAdminUserGroupToProject,
  removeUserFromProject,
  updateProjectUserRole,
} from '../constants';
import { canEditProjectUserRoles, hasPrivilege, isSuperAdmin, PRIVILEGES } from '../utils/permissions';
import '../assets/css/global.scss';

const getProjectId = (project) =>
  String(project?._id || project?.projectId || project?.id || '');

const getProjectName = (project) =>
  project?.projectName || project?.name || project?.key || 'Untitled Project';

const getCompanyId = (company) =>
  String(company?._id || company?.companyId || company?.id || '');

const getCompanyName = (company) =>
  company?.companyName || company?.name || 'Untitled Workspace';

const getAssigneeId = (user) =>
  String(user?._id || user?.userId || user?.id || user?.mappingId || '');

const getAssigneeName = (user) => user?.name || user?.userName || 'User';

const getCurrentUserIdentity = () => {
  try {
    const raw = sessionStorage.getItem('userData');
    if (!raw) return { id: '', email: '' };
    const parsed = JSON.parse(raw);
    return {
      id: String(parsed?._id || parsed?.id || parsed?.userId || parsed?.data?._id || '').trim(),
      email: String(parsed?.email || parsed?.data?.email || '')
        .trim()
        .toLowerCase(),
    };
  } catch {
    return { id: '', email: '' };
  }
};

/** True when the assignee card is the logged-in user (by userId or email). */
const isSelfAssignee = (user) => {
  const current = getCurrentUserIdentity();
  const assigneeUserId = String(user?.userId || (user?.status === 'pending' ? '' : user?._id) || '').trim();
  const assigneeEmail = String(user?.email || '')
    .trim()
    .toLowerCase();
  if (current.id && assigneeUserId && current.id === assigneeUserId) return true;
  if (current.email && assigneeEmail && current.email === assigneeEmail) return true;
  return false;
};

export default function AdminProjectAccess() {
  const isSa = isSuperAdmin();
  const canEditRoles = canEditProjectUserRoles() || hasPrivilege(PRIVILEGES.ADMIN_BLOCK);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectUsersMap, setProjectUsersMap] = useState({});
  const [projectUserCounts, setProjectUserCounts] = useState({});
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeProject, setActiveProject] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editProject, setEditProject] = useState(null);
  const [savingRole, setSavingRole] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeUser, setRemoveUser] = useState(null);
  const [removeProject, setRemoveProject] = useState(null);
  const [removingUser, setRemovingUser] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState(false);
  const [workspaceQuery, setWorkspaceQuery] = useState('');
  const workspacePickerRef = useRef(null);
  const selectedCompanyIdRef = useRef(selectedCompanyId);
  selectedCompanyIdRef.current = selectedCompanyId;

  // Super Admin always; project Admin only when Admin on more than one company
  const showCompanyPicker = isSa || companies.length > 1;

  const selectedCompany = useMemo(
    () => companies.find((c) => getCompanyId(c) === selectedCompanyId) || null,
    [companies, selectedCompanyId],
  );

  const filteredWorkspaces = useMemo(() => {
    const q = workspaceQuery.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => getCompanyName(c).toLowerCase().includes(q));
  }, [companies, workspaceQuery]);

  useEffect(() => {
    if (!workspaceMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (workspacePickerRef.current && !workspacePickerRef.current.contains(e.target)) {
        setWorkspaceMenuOpen(false);
        setWorkspaceQuery('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setWorkspaceMenuOpen(false);
        setWorkspaceQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [workspaceMenuOpen]);

  const filteredProjects = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((project) => {
      const name = getProjectName(project).toLowerCase();
      const key = String(project?.key || project?.projectKey || '').toLowerCase();
      return name.includes(q) || key.includes(q);
    });
  }, [projects, projectSearch]);

  const selectedProject = useMemo(
    () => filteredProjects.find((p) => getProjectId(p) === selectedProjectId)
      || projects.find((p) => getProjectId(p) === selectedProjectId)
      || null,
    [filteredProjects, projects, selectedProjectId],
  );

  const selectedAssignees = useMemo(
    () => (selectedProjectId ? projectUsersMap[selectedProjectId] || [] : []),
    [projectUsersMap, selectedProjectId],
  );

  /** Load assignees for one project only (detail panel). Sidebar counts use bulk API. */
  const loadUsersForProject = useCallback(async (projectId) => {
    const pid = String(projectId || '');
    if (!pid) return;
    try {
      const users = await getAdminProjectUsers(pid);
      const list = Array.isArray(users) ? users : users?.users || users?.data || [];
      setProjectUsersMap((prev) => ({ ...prev, [pid]: list }));
      setProjectUserCounts((prev) => ({ ...prev, [pid]: list.length }));
    } catch {
      setProjectUsersMap((prev) => ({ ...prev, [pid]: [] }));
      setProjectUserCounts((prev) => ({ ...prev, [pid]: 0 }));
    }
  }, []);

  const loadProjects = useCallback(async (companyId) => {
    if (!companyId) {
      setProjects([]);
      setProjectUsersMap({});
      setProjectUserCounts({});
      return;
    }
    setLoadingProjects(true);
    setProjectUsersMap({});
    setProjectUserCounts({});
    try {
      const [data, countsRes] = await Promise.all([
        getAdminProjects(companyId),
        getAdminProjectUserCounts(companyId).catch(() => ({ data: {} })),
      ]);
      const list = Array.isArray(data)
        ? data
        : data?.projects || data?.data || [];
      setProjects(list);
      const countsPayload = countsRes?.data || countsRes || {};
      setProjectUserCounts(
        countsPayload && typeof countsPayload === 'object' && !Array.isArray(countsPayload)
          ? countsPayload
          : {},
      );
    } catch (err) {
      setProjects([]);
      setProjectUsersMap({});
      setProjectUserCounts({});
      toast.error(err?.response?.data?.message || 'Failed to load projects.');
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  // Fetch users only for the selected project
//   useEffect(() => {
//     if (!selectedProjectId) return undefined;
//     let cancelled = false;
//     (async () => {
//       try {
// <<<<<<< Updated upstream
//         const data = await getAdminProjects(companyId);
//         const list = Array.isArray(data)
//           ? data
//           : data?.projects || data?.data || [];
//         setProjects(list);
//         setScopeMessage(
//           isSa ? '' : data?.scopeMessage || '',
//         );
//         setAdminProjectNames(
//           isSa
//             ? []
//             : Array.isArray(data?.adminProjectNames)
//               ? data.adminProjectNames
//               : [],
//         );
//         await loadProjectUsers(list);
//         if (!isSa && String(companyId) === String(selectedCompanyIdRef.current)) {
//           setAdminInfo((prev) => {
//             if (!prev) return prev;
//             const integrated =
//               Boolean(prev.onboardingStatus?.firstProjectIntegrated) || list.length > 0;
//             if (prev.onboardingStatus?.firstProjectIntegrated === integrated) return prev;
//             return {
//               ...prev,
//               onboardingStatus: {
//                 ...prev.onboardingStatus,
//                 firstProjectIntegrated: integrated,
//               },
//             };
//           });
//         }
//       } catch (err) {
//         setProjects([]);
//         setProjectUsersMap({});
//         setScopeMessage('');
//         setAdminProjectNames([]);
//         toast.error(err?.response?.data?.message || 'Failed to load projects.');
//       } finally {
//         setLoadingProjects(false);
// =======
//         const users = await getAdminProjectUsers(selectedProjectId);
//         if (cancelled) return;
//         const list = Array.isArray(users) ? users : users?.users || users?.data || [];
//         setProjectUsersMap((prev) => ({ ...prev, [selectedProjectId]: list }));
//       } catch {
//         if (!cancelled) {
//           setProjectUsersMap((prev) => ({ ...prev, [selectedProjectId]: [] }));
//         }
// >>>>>>> Stashed changes
//       }
//     })();
//     return () => {
//       cancelled = true;
//     };
//   }, [selectedProjectId]);

useEffect(() => {
  if (!selectedProjectId) return undefined;
  let cancelled = false;
  (async () => {
    try {
      const users = await getAdminProjectUsers(selectedProjectId);
      if (cancelled) return;
      const list = Array.isArray(users) ? users : users?.users || users?.data || [];
      setProjectUsersMap((prev) => ({ ...prev, [selectedProjectId]: list }));
    } catch {
      if (!cancelled) {
        setProjectUsersMap((prev) => ({ ...prev, [selectedProjectId]: [] }));
      }
    }
  })();
  return () => {
    cancelled = true;
  };
}, [selectedProjectId]);

  useEffect(() => {
    if (isSa || !selectedCompanyId) {
      setAdminInfo(null);
      return undefined;
    }

    let cancelled = false;
    setAdminInfo(null);

    const fetchAdminInfo = async () => {
      try {
        const response = await workspaceService.getAdminDashboard(selectedCompanyId);
        if (
          response?.success
          && !cancelled
          && String(selectedCompanyId) === String(selectedCompanyIdRef.current)
        ) {
          const data = response.data || {};
          setAdminInfo({
            ...data,
            workspaceName:
              data.workspaceName ||
              (selectedCompany ? getCompanyName(selectedCompany) : '—'),
            onboardingStatus: {
              ...(data.onboardingStatus || {}),
              firstProjectIntegrated: Boolean(data?.onboardingStatus?.firstProjectIntegrated),
            },
          });
        }
      } catch (err) {
        console.error('Error fetching admin info:', err);
        if (!cancelled) {
          let role = 'Admin';
          try {
            const raw = sessionStorage.getItem('userData');
            const parsed = raw ? JSON.parse(raw) : {};
            role = parsed?.role || 'Admin';
          } catch {
            /* ignore */
          }
          setAdminInfo({
            workspaceName: selectedCompany ? getCompanyName(selectedCompany) : '—',
            role,
            onboardingStatus: {
              firstProjectIntegrated: false,
            },
          });
        }
      }
    };
    fetchAdminInfo();
    return () => {
      cancelled = true;
    };
  }, [isSa, selectedCompanyId, selectedCompany]);

  useEffect(() => {
    let cancelled = false;
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const data = await getAdminCompanies();
        if (cancelled) return;
        // Backend may resolve effective role as Super Admin (e.g. company.superAdminId)
        if (data?.role) {
          try {
            const raw = sessionStorage.getItem('userData');
            const parsed = raw ? JSON.parse(raw) : {};
            if (parsed.role !== data.role) {
              sessionStorage.setItem('userData', JSON.stringify({ ...parsed, role: data.role }));
              window.dispatchEvent(
                new CustomEvent('qmetrix-role-updated', { detail: { role: data.role } }),
              );
            }
          } catch {
            /* ignore */
          }
        }
        const list = Array.isArray(data) ? data : data?.companies || data?.data || [];
        setCompanies(list);
        // Prefer session company if present in the list; else first Meta company
        const sessionCompanyId = sessionStorage.getItem('companyId');
        const preferred =
          (sessionCompanyId &&
            list.find((c) => getCompanyId(c) === String(sessionCompanyId))) ||
          list[0];
        if (preferred) {
          setSelectedCompanyId(getCompanyId(preferred));
        }
      } catch (err) {
        if (!cancelled) {
          setCompanies([]);
          toast.error(err?.response?.data?.message || 'Failed to load companies.');
        }
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    };
    fetchCompanies();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      loadProjects(selectedCompanyId);
      setProjectSearch('');
      setSelectedProjectId('');
    }
  }, [selectedCompanyId, loadProjects]);

  // Keep a project selected when list / search changes
  useEffect(() => {
    if (!filteredProjects.length) {
      if (selectedProjectId) setSelectedProjectId('');
      return;
    }
    const stillVisible = filteredProjects.some((p) => getProjectId(p) === selectedProjectId);
    if (!stillVisible) {
      setSelectedProjectId(getProjectId(filteredProjects[0]));
    }
  }, [filteredProjects, selectedProjectId]);

  const openAddModal = (project) => {
    setActiveProject(project);
    setModalOpen(true);
  };

  const closeAddModal = () => {
    setModalOpen(false);
    setActiveProject(null);
  };

  const handleInvite = async ({ email, role }) => {
    if (!activeProject || !selectedCompanyId) return;
    const projectId = getProjectId(activeProject);
    setAssigning(true);
    try {
      const result = await inviteAdminEmployee({
        email,
        role,
        companyId: selectedCompanyId,
        projectId,
      });
      const payload = result?.data || result;
      if (payload?.emailSent === false) {
        toast.warn(
          payload?.message || 'User assigned. Notification email could not be sent.',
        );
      } else {
        toast.success(payload?.message || result?.message || 'User assigned successfully.');
      }
      closeAddModal();
      await loadUsersForProject(projectId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send invitation.');
    } finally {
      setAssigning(false);
    }
  };

  const handleApplyGroup = async ({ groupId, companyId, projectId }) => {
    if (!groupId || !companyId || !projectId) return;
    setAssigning(true);
    try {
      const result = await applyAdminUserGroupToProject(groupId, { companyId, projectId });
      const payload = result?.data || result;
      const failCount = payload?.failCount || 0;
      if (failCount > 0) {
        toast.warn(result?.message || payload?.message || 'Group applied with some failures.');
      } else {
        toast.success(result?.message || payload?.message || 'User group added to project.');
      }
      closeAddModal();
      await loadUsersForProject(projectId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to apply user group.');
    } finally {
      setAssigning(false);
    }
  };

  const openRemoveModal = (project, user) => {
    setRemoveProject(project);
    setRemoveUser(user);
    setRemoveModalOpen(true);
  };

  const closeRemoveModal = () => {
    if (removingUser) return;
    setRemoveModalOpen(false);
    setRemoveUser(null);
    setRemoveProject(null);
  };

  const handleConfirmRemove = async () => {
    if (!removeProject || !removeUser) return;
    const mappingId = removeUser?.mappingId || removeUser?._id || removeUser?.id;
    const projectId = getProjectId(removeProject);
    const userId = getAssigneeId(removeUser);
    setRemovingUser(true);
    try {
      await removeUserFromProject(mappingId || userId, { projectId, userId });
      toast.success('Employee removed from project.');
      setProjectUsersMap((prev) => {
        const nextList = (prev[projectId] || []).filter(
          (u) => getAssigneeId(u) !== userId,
        );
        setProjectUserCounts((countsPrev) => ({
          ...countsPrev,
          [projectId]: nextList.length,
        }));
        return { ...prev, [projectId]: nextList };
      });
      setRemoveModalOpen(false);
      setRemoveUser(null);
      setRemoveProject(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove employee.');
    } finally {
      setRemovingUser(false);
    }
  };

  const openEditRoleModal = (project, user) => {
    setEditProject(project);
    setEditUser(user);
    setEditModalOpen(true);
  };

  const closeEditRoleModal = () => {
    setEditModalOpen(false);
    setEditUser(null);
    setEditProject(null);
  };

  const handleSaveRole = async (role) => {
    const mappingId = editUser?.mappingId || null;
    const projectId = getProjectId(editProject);
    const userId =
      editUser?.userId ||
      (editUser?.status === 'pending' ? null : editUser?._id);
    const email = editUser?.email;

    if (!projectId || (!userId && !email && !mappingId)) {
      toast.error('Unable to update role — missing user or project.');
      return;
    }
    if (!selectedCompanyId) {
      toast.error('Unable to update role — missing company.');
      return;
    }

    setSavingRole(true);
    try {
      let result;
      if (userId) {
        result = await updateProjectUserRole(mappingId, role, {
          projectId,
          userId: String(userId),
          companyId: selectedCompanyId,
        });
      } else if (email) {
        // Pending invite / email-only — use invite path (already on server)
        result = await inviteAdminEmployee({
          email,
          role,
          companyId: selectedCompanyId,
          projectId,
        });
      } else {
        throw new Error('Missing user id');
      }
      toast.success(result?.message || 'Role updated successfully.');
      closeEditRoleModal();
      await loadUsersForProject(projectId);
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      toast.error(apiMessage || err?.message || 'Failed to update role.');
    } finally {
      setSavingRole(false);
    }
  };

  return (
    <AdminLayout title="Admin">
      <div className="admin-project-access flex flex-col flex-1 min-h-0 w-full max-w-full min-w-0">
        {/* Admin Info Section — For Admin role only */}
        {!isSa && adminInfo && (
          <div className="px-[18px] pb-4">
            <div className="bg-white dark:bg-[#182433] border border-[#D1E2F0] dark:border-[#25384F] rounded-lg px-4 py-4">
              <h3 className="text-sm font-semibold text-[#0A2342] dark:text-white mb-3">Your Workspace Info</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-[#6B7C93] dark:text-[#8FA3B8] font-medium mb-1">Workspace</p>
                  <p className="text-sm font-medium text-[#0A2342] dark:text-white">{adminInfo?.workspaceName || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B7C93] dark:text-[#8FA3B8] font-medium mb-1">Role</p>
                  <span className="inline-block px-2.5 py-1 text-xs font-medium rounded-md bg-[#d1fae5] text-[#065f46] dark:bg-[#1a5e4a] dark:text-[#6ee7b7]">
                    {adminInfo?.role || '—'}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-[#6B7C93] dark:text-[#8FA3B8] font-medium mb-1">First Project Integrated</p>
                  <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-md ${adminInfo?.onboardingStatus?.firstProjectIntegrated ? 'bg-[#d1fae5] text-[#065f46] dark:bg-[#1a5e4a] dark:text-[#6ee7b7]' : 'bg-[#fef3c7] text-[#92400e] dark:bg-[#78350f] dark:text-[#fcd34d]'}`}>
                    {adminInfo?.onboardingStatus?.firstProjectIntegrated ? '✓ Yes' : '○ No'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="px-[18px] pb-3 flex-1 min-h-0 flex flex-col gap-2.5 overflow-hidden">
          {/* Compact toolbar: workspace + search */}
          <div className="shrink-0 bg-white dark:bg-[#182433] border border-[#D1E2F0] dark:border-[#25384F] rounded-lg px-4 py-3 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col sm:flex-row gap-3 flex-1 min-w-0">
              {showCompanyPicker && (
                <div className="w-full sm:max-w-xs min-w-0" ref={workspacePickerRef}>
                  <label className="block text-sm font-medium text-[#0A2342] dark:text-white mb-1.5">
                    Workspace
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      disabled={loadingCompanies || !companies.length}
                      onClick={() => {
                        setWorkspaceMenuOpen((open) => !open);
                        setWorkspaceQuery('');
                      }}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-[#0A2342] dark:text-white text-sm outline-none focus:border-[#326AEB] disabled:opacity-50 text-left"
                    >
                      <span className="truncate">
                        {loadingCompanies
                          ? 'Loading workspaces...'
                          : !companies.length
                            ? 'No workspaces found'
                            : selectedCompany
                              ? getCompanyName(selectedCompany)
                              : 'Select workspace'}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 shrink-0 text-[#8FA3B8] transition-transform ${
                          workspaceMenuOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {workspaceMenuOpen && !loadingCompanies && companies.length > 0 && (
                      <div className="absolute z-30 mt-1 w-full rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] shadow-lg overflow-hidden">
                        <div className="p-2 border-b border-[#D1E2F0] dark:border-[#25384F]">
                          <div className="relative">
                            <Search
                              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8FA3B8]"
                              aria-hidden
                            />
                            <input
                              type="text"
                              autoFocus
                              value={workspaceQuery}
                              onChange={(e) => setWorkspaceQuery(e.target.value)}
                              placeholder="Search workspace..."
                              style={{ paddingLeft: '2rem' }}
                              className="w-full py-2 pr-2 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#0D1621] text-[#0A2342] dark:text-white text-sm outline-none focus:border-[#326AEB] placeholder:text-[#8FA3B8]"
                            />
                          </div>
                        </div>
                        <ul className="max-h-56 overflow-y-auto py-1">
                          {filteredWorkspaces.length === 0 ? (
                            <li className="px-3 py-2 text-sm text-[#6B7C93] dark:text-[#8FA3B8]">
                              No workspaces match
                            </li>
                          ) : (
                            filteredWorkspaces.map((company) => {
                              const id = getCompanyId(company);
                              const active = id === selectedCompanyId;
                              return (
                                <li key={id}>
                                  <button
                                    type="button"
                                    title={getCompanyName(company)}
                                    onClick={() => {
                                      setWorkspaceMenuOpen(false);
                                      setWorkspaceQuery('');
                                      if (String(id) === String(selectedCompanyId)) {
                                        return;
                                      }
                                      setSelectedCompanyId(id);
                                      setProjects([]);
                                      setProjectUsersMap({});
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm truncate ${
                                      active
                                        ? 'bg-[#326AEB] text-white'
                                        : 'text-[#0A2342] dark:text-white hover:bg-[#E8F1F8] dark:hover:bg-[#25384F]'
                                    }`}
                                  >
                                    {getCompanyName(company)}
                                  </button>
                                </li>
                              );
                            })
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="w-full sm:max-w-sm min-w-0">
                <label className="block text-sm font-medium text-[#0A2342] dark:text-white mb-1.5">
                  Search projects
                </label>
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8FA3B8]"
                    aria-hidden
                  />
                  <input
                    type="text"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    placeholder="Filter by name..."
                    disabled={loadingProjects || !projects.length}
                    style={{ paddingLeft: '2.75rem' }}
                    className="w-full py-2.5 pr-3 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-[#0A2342] dark:text-white text-sm outline-none focus:border-[#326AEB] placeholder:text-[#8FA3B8] disabled:opacity-50"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Master–detail: both panes scroll internally; page stays fixed */}
          <div className="flex-1 min-h-0 bg-white dark:bg-[#182433] border border-[#D1E2F0] dark:border-[#25384F] rounded-lg overflow-hidden grid grid-cols-1 md:grid-cols-[300px_minmax(0,1fr)] lg:grid-cols-[320px_minmax(0,1fr)]">
            {/* Left: project list (own scroll) */}
            <aside className="min-h-0 border-b md:border-b-0 md:border-r border-[#D1E2F0] dark:border-[#25384F] flex flex-col overflow-hidden max-h-[38vh] md:max-h-none">
              <div className="px-3 py-2.5 border-b border-[#D1E2F0] dark:border-[#25384F] flex items-center justify-end gap-2 shrink-0">
                <FolderKanban className="w-4 h-4 shrink-0 text-[#326AEB] dark:text-[#78B5FF]" />
                <p className="text-xs font-semibold uppercase tracking-wide text-right text-[#6B7C93] dark:text-[#8FA3B8] truncate">
                  {loadingProjects
                    ? 'Projects'
                    : `${projects.length} ${projects.length === 1 ? 'project' : 'projects'}`}
                </p>
              </div>

              {loadingProjects && (
                <p className="px-3 py-8 text-center text-sm text-[#6B7C93] dark:text-[#8FA3B8]">
                  Loading projects...
                </p>
              )}
              {!loadingProjects && !projects.length && (
                <p className="px-3 py-8 text-center text-sm text-[#6B7C93] dark:text-[#8FA3B8]">
                  No projects found for this company.
                </p>
              )}
              {!loadingProjects && projects.length > 0 && filteredProjects.length === 0 && (
                <p className="px-3 py-8 text-center text-sm text-[#6B7C93] dark:text-[#8FA3B8]">
                  No matches for “{projectSearch.trim()}”.
                </p>
              )}

              {!loadingProjects && filteredProjects.length > 0 && (
                <ul className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-1">
                  {filteredProjects.map((project) => {
                    const projectId = getProjectId(project);
                    const count = Number(projectUserCounts[projectId] ?? 0);
                    const active = selectedProjectId === projectId;
                    return (
                      <li key={projectId}>
                        <button
                          type="button"
                          onClick={() => setSelectedProjectId(projectId)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors rounded-none ${
                            active
                              ? 'bg-[#24527A] dark:bg-[#326AEB] text-white'
                              : 'hover:bg-[#F7FAFC] dark:hover:bg-[#1A2738] text-[#0A2342] dark:text-[#D9E4F1]'
                          }`}
                        >
                          <span
                            className={`flex-1 min-w-0 text-sm truncate ${
                              active ? 'font-semibold text-white' : 'font-medium'
                            }`}
                            title={getProjectName(project)}
                          >
                            {getProjectName(project)}
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded text-[11px] ${
                              active
                                ? 'bg-white/20 text-white'
                                : 'bg-[#F0F4F8] dark:bg-[#151F2C] text-[#6B7C93] dark:text-[#8FA3B8]'
                            }`}
                          >
                            <Users className="w-3 h-3" />
                            {count}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </aside>

            {/* Right: selected project users (own scroll when many) */}
            <section className="min-w-0 min-h-0 flex flex-col overflow-hidden">
              {!selectedProject ? (
                <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
                  <FolderKanban className="w-10 h-10 text-[#D1E2F0] dark:text-[#25384F] mb-3" />
                  <p className="text-sm font-medium text-[#0A2342] dark:text-white">
                    Select a project
                  </p>
                  <p className="mt-1 text-xs text-[#6B7C93] dark:text-[#8FA3B8] max-w-sm">
                    Choose a project on the left to view and manage assigned users.
                  </p>
                </div>
              ) : (
                <>
                  <div className="px-4 py-3 border-b border-[#D1E2F0] dark:border-[#25384F] flex items-center justify-between gap-3 shrink-0">
                    <div className="min-w-0">
                      <h2
                        className="text-base font-semibold text-[#0A2342] dark:text-white truncate"
                        title={getProjectName(selectedProject)}
                      >
                        {getProjectName(selectedProject)}
                      </h2>
                      <p className="text-xs text-[#6B7C93] dark:text-[#8FA3B8] mt-0.5">
                        {selectedAssignees.length} user
                        {selectedAssignees.length === 1 ? '' : 's'} assigned
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => openAddModal(selectedProject)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md text-white bg-[#326AEB] hover:bg-[#2457c5] dark:hover:bg-[#4a7ef0] transition-colors shrink-0"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add user
                    </button>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4">
                    {selectedAssignees.length === 0 ? (
                      <div className="h-full min-h-[160px] flex flex-col items-center justify-center rounded-lg border border-dashed border-[#D1E2F0] dark:border-[#25384F] px-6 py-8 text-center">
                        <Users className="w-8 h-8 text-[#D1E2F0] dark:text-[#25384F] mb-2" />
                        <p className="text-sm font-medium text-[#0A2342] dark:text-white">
                          No users assigned
                        </p>
                        <p className="mt-1 text-xs text-[#6B7C93] dark:text-[#8FA3B8] max-w-xs">
                          Invite a single user{canEditRoles ? ' or apply a user group' : ''} to this project.
                        </p>
                        <button
                          type="button"
                          onClick={() => openAddModal(selectedProject)}
                          className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md text-[#24527A] dark:text-[#78B5FF] bg-[#E8F1F8] dark:bg-[#25384F] hover:bg-[#D6E8F5] dark:hover:bg-[#2E445E]"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add user
                        </button>
                      </div>
                    ) : (
                      <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
                        {selectedAssignees.map((user) => {
                          const userId = getAssigneeId(user);
                          const isPending = user.status === 'pending';
                          const showRemove = isSa || !isSelfAssignee(user);
                          return (
                            <li
                              key={userId}
                              className="group flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-[#F7FAFC] dark:bg-[#151F2C] border border-[#E8EEF5] dark:border-[#25384F] hover:border-[#D1E2F0] dark:hover:border-[#415061]"
                            >
                              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-[#25384F] text-[#24527A] dark:text-[#78B5FF] shrink-0 border border-[#E8EEF5] dark:border-[#2E445E]">
                                <User className="w-3.5 h-3.5" />
                              </span>
                              <span className="flex-1 min-w-0">
                                <span
                                  className="block text-sm font-medium text-[#0A2342] dark:text-white truncate"
                                  title={getAssigneeName(user)}
                                >
                                  {getAssigneeName(user)}
                                </span>
                                {user.email ? (
                                  <span
                                    className="block text-[11px] text-[#6B7C93] dark:text-[#8FA3B8] truncate"
                                    title={user.email}
                                  >
                                    {user.email}
                                  </span>
                                ) : null}
                                <span className="flex items-center gap-1.5 flex-wrap mt-0.5">
                                  <span className="text-[11px] text-[#6B7C93] dark:text-[#8FA3B8] truncate">
                                    {user.role || '—'}
                                  </span>
                                  {isPending ? (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide text-red-600 bg-red-50 border border-red-200 dark:text-red-400 dark:bg-red-950/40 dark:border-red-800">
                                      Pending
                                    </span>
                                  ) : null}
                                </span>
                              </span>
                              <span className="flex items-center gap-0.5 shrink-0">
                                {canEditRoles && (
                                  <button
                                    type="button"
                                    onClick={() => openEditRoleModal(selectedProject, user)}
                                    className="p-1 rounded text-[#6B7C93] hover:text-[#326AEB] dark:text-[#8FA3B8] dark:hover:text-[#78B5FF]"
                                    title="Edit role"
                                    aria-label={`Edit role for ${getAssigneeName(user)}`}
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {showRemove && (
                                  <button
                                    type="button"
                                    onClick={() => openRemoveModal(selectedProject, user)}
                                    className="p-1 rounded text-[#6B7C93] hover:text-red-500 dark:text-[#8FA3B8] dark:hover:text-red-400"
                                    title="Remove"
                                    aria-label={`Remove ${getAssigneeName(user)}`}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
      </div>

      <AddEmployeeToProjectModal
        isOpen={modalOpen}
        onClose={closeAddModal}
        project={activeProject}
        companyId={selectedCompanyId}
        onInvite={handleInvite}
        onApplyGroup={handleApplyGroup}
        assigning={assigning}
        allowUserGroups={canEditRoles}
      />

      <EditProjectUserRoleModal
        isOpen={editModalOpen}
        onClose={closeEditRoleModal}
        user={editUser}
        project={editProject}
        companyId={selectedCompanyId}
        onSave={handleSaveRole}
        saving={savingRole}
      />

      <RemoveProjectUserConfirmModal
        isOpen={removeModalOpen}
        onClose={closeRemoveModal}
        user={removeUser}
        project={removeProject}
        onConfirm={handleConfirmRemove}
        removing={removingUser}
      />
    </AdminLayout>
  );
}
